import React from "react";
import { Handle, Position } from "@xyflow/react";
import { emittedHandleIds, portHandleDefs, portSide } from "./ports.js";
import { trunc } from "./text.js";

export { trunc } from "./text.js";
export { portSide };

function HandleBank({ ports, showLabels }) {
  const defs = portHandleDefs(ports);
  const labels = [];
  const seen = new Set();
  for (const h of defs) {
    if (h.type !== "source" || seen.has(h.name)) continue;
    seen.add(h.name);
    labels.push(h);
  }
  const overflow = Math.max(0, (ports || []).length - emittedHandleIds(ports).size);
  return (
    <>
      {defs.map((h) => (
        <Handle
          key={`${h.name}-${h.type}`}
          id={h.id}
          type={h.type}
          position={h.position}
          className={`handle-dot ${h.kind === "power" ? "power" : ""}`}
          style={h.style}
        />
      ))}
      {showLabels
        ? labels.map((h) => {
            const side =
              h.position === Position.Left
                ? "left"
                : h.position === Position.Right
                  ? "right"
                  : h.position === Position.Top
                    ? "top"
                    : "bottom";
            return (
              <span
                key={`lab-${h.name}`}
                className={`handle-lab ${side} ${h.kind === "power" ? "power" : ""}`}
                style={h.style}
              >
                {h.name}
              </span>
            );
          })
        : null}
      {overflow > 0 ? <div className="overflow">+{overflow} more</div> : null}
    </>
  );
}

function EnterCtl({ data }) {
  if (!data.canEnter) return null;
  return (
    <button
      type="button"
      className="enter-ctl"
      onClick={(e) => {
        e.stopPropagation();
        data.onEnter?.();
      }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      Enter
    </button>
  );
}

export const SheetNode = React.memo(function SheetNode({ data, selected }) {
  const ports = data.ports || [];
  const kidN = (data.children || []).length;
  const metaBits = [
    data.role,
    data.enterTarget && data.enterTarget !== data.name ? `→ ${data.enterTarget}` : kidN ? `${kidN} blocks` : null,
  ].filter(Boolean);
  return (
    <div
      className={`mn sheet ${selected ? "selected" : ""} ${data.isRoot ? "root" : ""} ${data.diag ? "diag" : ""}`}
      style={{ borderTop: `3px solid ${data.color || "#64748b"}` }}
    >
      <HandleBank ports={ports} showLabels={data.showPorts} />
      <div className="hd">
        <div className="kicker">{data.isRoot ? "ROOT SHEET" : data.role === "sheet_instance" ? "INSTANCE" : "SHEET"}</div>
        <div className="name">{data.name}</div>
        <div className="meta">{metaBits.join(" · ")}</div>
      </div>
      {data.intent ? <div className="intent">{trunc(data.intent, 110)}</div> : null}
      <EnterCtl data={data} />
    </div>
  );
});

export const BlockNode = React.memo(function BlockNode({ data, selected }) {
  const kind = data.subcircuitKind || data.kind || "leaf";
  const ports = data.ports || [];
  return (
    <div
      className={`mn leaf kind-${kind} ${selected ? "selected" : ""} ${data.diag ? "diag" : ""}`}
      style={{ borderTop: `3px solid ${data.color || "#334155"}` }}
    >
      <HandleBank ports={ports} showLabels={data.showPorts} />
      <div className="hd">
        <div className="kicker">{String(kind).replace(/_/g, " ")}</div>
        <div className="name">{data.name}</div>
        <div className="meta">{[data.role, data.mpn].filter(Boolean).join(" · ")}</div>
      </div>
      {data.intent ? <div className="intent">{trunc(data.intent, 80)}</div> : null}
      <EnterCtl data={data} />
    </div>
  );
});

export const nodeTypes = { sheet: SheetNode, mind: BlockNode };
